import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DirectSaleHeader } from '@/components/direct-sale/DirectSaleHeader';
import { DirectSaleProductStep } from '@/components/direct-sale/DirectSaleProductStep';
import { DirectSaleDesignStep } from '@/components/direct-sale/DirectSaleDesignStep';
import { DirectSaleCheckoutStep } from '@/components/direct-sale/DirectSaleCheckoutStep';
import { DirectSaleResumeDialog } from '@/components/direct-sale/DirectSaleResumeDialog';
import {
  hasResumableSession,
  useDirectSaleStore,
} from '@/stores/directSaleStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { clearSession, ensureSession } from '@/api/directSale';

/**
 * Entry point of the Direct Sale flow.
 *
 * Runs on its own route with its own shell rather than as a mode of the
 * Seller Creator: the actor, the step sequence, the ending and the pricing
 * all differ. The editor core underneath is shared, not duplicated.
 *
 * Access is granted by the embedding shop's signature on the URL
 * (?shop=&ts=&sig=), verified server-side. The end customer never
 * authenticates.
 */
export function DirectSalePage() {
  const [searchParams] = useSearchParams();
  const currentStep = useDirectSaleStore((s) => s.currentStep);
  const productId = useDirectSaleStore((s) => s.productId);
  const [resumePrompt, setResumePrompt] = useState(false);

  const shop = searchParams.get('shop');

  /**
   * Fresh start for this shop: drop the persisted selection AND the
   * persisted canvas — the canvas manager keeps masterPositions in
   * localStorage, so without this an earlier session's design would mark
   * positions as decorated and leak into this customer's checkout.
   */
  const startFresh = useCallback((forShop: string | null) => {
    useDirectSaleStore.getState().reset();
    try {
      useCanvasStateManager.getState().clearAllPositions();
    } catch {
      // Canvas manager not initialized yet; nothing to clear.
    }
    useDirectSaleStore.getState().setShop(forShop);
  }, []);

  useEffect(() => {
    // Trade the ±300s URL signature for an 8h session token right away —
    // waiting until the first quote call would race the signature's expiry
    // against however long the customer designs.
    clearSession();
    void ensureSession().catch(() => {
      // Individual API calls surface their own errors; nothing to do here.
    });

    // Same shop, recent work, product chosen: the customer decides whether
    // to pick it up — nothing of the old session is cleared or rendered
    // until they do. Anything else (other shop, stale, empty) starts fresh.
    if (hasResumableSession(useDirectSaleStore.getState(), shop)) {
      setResumePrompt(true);
      return;
    }
    startFresh(shop);
  }, [shop, startFresh]);

  const handleResume = () => {
    setResumePrompt(false);
    // The step itself is never persisted; a resumed session re-enters at
    // the design step with selection and canvas restored.
    useDirectSaleStore.getState().goToStep('design');
  };

  const handleRestart = () => {
    startFresh(shop);
    setResumePrompt(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <DirectSaleHeader />

      <main className="flex flex-1 flex-col">
        {currentStep === 'product' && <DirectSaleProductStep />}
        {/* The design step stays MOUNTED (hidden) across step changes.
            Unmounting rebuilds the canvas, and the editor core's
            switchPosition saves-then-loads on that rebuild — persisting the
            fresh empty canvas OVER the customer's design before the load
            can read it. Keeping the canvas alive sidesteps the whole
            save/restore cycle and makes step returns instant. Keyed by
            product so a product switch still gets a fresh editor. */}
        {productId !== null && (
          <div
            key={productId}
            className={
              currentStep === 'design' ? 'flex flex-1 flex-col' : 'hidden'
            }
          >
            <DirectSaleDesignStep />
          </div>
        )}
        {currentStep === 'checkout' && <DirectSaleCheckoutStep />}
      </main>

      {resumePrompt && (
        <DirectSaleResumeDialog
          onResume={handleResume}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}

export default DirectSalePage;
